import React from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";

import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingShop from "@/pages/OnboardingShop";
import SplashScreen from "@/components/SplashScreen";
import ComparePricesPage from "@/pages/ComparePricesPage";
import BusBookingPage from "@/pages/customer/BusBookingPage";
import { PendingPage, RejectedPage } from "@/pages/StatusGates";
import DashboardPage from "@/pages/DashboardPage";
import PostsPage from "@/pages/PostsPage";
import PostDetailPage from "@/pages/PostDetailPage";
import PostCreatePage from "@/pages/PostCreatePage";
import MyPostsPage from "@/pages/MyPostsPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminShopsPendingPage from "@/pages/AdminShopsPendingPage";
import AdminWaitlistPage from "@/pages/AdminWaitlistPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminPartnerShopsPage from "@/pages/AdminPartnerShopsPage";
import AdminMerchantAdsPage from "@/pages/AdminMerchantAdsPage";
import AdminPartnerBrandsPage from "@/pages/AdminPartnerBrandsPage";
import AdminPostsPage from "@/pages/AdminPostsPage";
import SettingsPage from "@/pages/SettingsPage";
import SmartHomePage from "@/pages/SmartHomePage";

// Revenue admin
import RevenueGuard from "@/pages/admin/revenue/RevenueGuard";
import RevenueHomePage from "@/pages/admin/revenue/RevenueHomePage";
import PackagesPage from "@/pages/admin/revenue/PackagesPage";
import SubscriptionsPage from "@/pages/admin/revenue/SubscriptionsPage";
import TransactionsPage from "@/pages/admin/revenue/TransactionsPage";
import CouponTopupsPage from "@/pages/admin/revenue/CouponTopupsPage";
import ReportsPage from "@/pages/admin/revenue/ReportsPage";

// Ads admin (Phase 4b)
import AdsOverviewPage from "@/pages/admin/ads/AdsOverview";
import BannersManagerPage from "@/pages/admin/ads/BannersManager";
import VideoQueuePage from "@/pages/admin/ads/VideoQueue";
import SponsoredListingsPage from "@/pages/admin/ads/SponsoredListings";

// Finance admin (Phase 5b) — SUPER_ADMIN + ACCOUNTS
import FinanceOverviewPage from "@/pages/admin/finance/FinanceOverview";
import ExpensesPage from "@/pages/admin/finance/ExpensesPage";
import PnlPage from "@/pages/admin/finance/PnlPage";
import AiAccountantPage from "@/pages/admin/finance/AiAccountantPage";

// Partner Click Report (Phase 6) — SUPER_ADMIN + ACCOUNTS
import PartnerClicksReportPage from "@/pages/admin/reports/PartnerClicksReport";
// Phase 7b — Customer (Smart Shopper) + admin trending + merchant scan
import CustomerLogin from "@/pages/customer/CustomerLogin";
import CustomerRegister from "@/pages/customer/CustomerRegister";
import CustomerCompleteProfile from "@/pages/customer/CustomerCompleteProfile";
import CustomerFavorites from "@/pages/customer/CustomerFavorites";
import CustomerCoupons from "@/pages/customer/CustomerCoupons";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import { CustomerLayout } from "@/components/customer/CustomerLayout";
import MerchantScan from "@/pages/merchant/MerchantScan";
import TrendingReport from "@/pages/admin/reports/TrendingReport";
// Phase 9a — Onboarding
import Onboarding from "@/pages/Onboarding";
import CustomerHome from "@/pages/CustomerHome";
import CustomerPartnerShopsPage from "@/pages/CustomerPartnerShopsPage";
import CustomerPartnerShopDetailPage from "@/pages/CustomerPartnerShopDetailPage";
// Phase 14 — Customer welcome onboarding (splash + discover + savings)
import OnboardingWelcome from "@/pages/OnboardingWelcome";
import { getLocalLocation } from "@/lib/location";
import { CityChip } from "@/components/customer/CityChip";

// Merchant portal
import MerchantLogin from "@/pages/merchant/MerchantLogin";
import MerchantRegister from "@/pages/merchant/MerchantRegister";
import MerchantOnboarding from "@/pages/merchant/MerchantOnboarding";
import MerchantHome from "@/pages/merchant/MerchantHome";
import MerchantOffers from "@/pages/merchant/MerchantOffers";
import MerchantAdsPage from "@/pages/merchant/MerchantAdsPage";
import MerchantOfferNew from "@/pages/merchant/MerchantOfferNew";
import MerchantOfferDetail from "@/pages/merchant/MerchantOfferDetail";
import MerchantCoupons from "@/pages/merchant/MerchantCoupons";
import MerchantProfile from "@/pages/merchant/MerchantProfile";
import MerchantVideos from "@/pages/merchant/MerchantVideos";

import "@/App.css";
import "@/index.css";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const shopBusinessInfoComplete = (u) =>
    !!u?.business_name &&
    !!u?.business_category &&
    !!u?.business_district &&
    !!u?.business_type &&
    !!u?.mobile_number;

const shopHasRequiredDocs = (u) => {
    const d = u?.verification_docs || {};
    return !!d.shop_business_license && !!d.pan_or_aadhaar;
};

/** Returns the correct route for a SHOP_OWNER user given their state, or null
 * if the current path is acceptable / no redirect needed. */
const shopOwnerRedirect = (user, pathname) => {
    if (user.role !== "SHOP_OWNER") return null;
    // The following are always allowed to stay put:
    const alwaysAllow = new Set([
        "/pending",
        "/rejected",
        "/settings",
        "/onboarding/shop",
        "/merchant/onboarding",
    ]);
    if (alwaysAllow.has(pathname)) return null;

    if (!shopBusinessInfoComplete(user)) return "/onboarding/shop";
    if (!shopHasRequiredDocs(user)) return "/merchant/onboarding";
    if (user.status === "PENDING_APPROVAL") return "/pending";
    if (user.status === "REJECTED") return "/rejected";
    if (user.status === "SUSPENDED") return "/rejected";
    // ACTIVE shop owners must stay under /merchant/*
    if (!pathname.startsWith("/merchant")) return "/merchant/home";
    return null;
};

/** Auth wrapper for /merchant/* routes (except register/login which are public). */
const MerchantProtected = ({ children }) => {
    const { user, status } = useAuth();
    const location = useLocation();

    if (status === "checking") return <FullscreenLoader />;
    if (status !== "authenticated" || !user) {
        return <Navigate to="/merchant/login" replace state={{ from: location }} />;
    }
    if (user.role !== "SHOP_OWNER") {
        return <Navigate to="/dashboard" replace />;
    }
    const redirect = shopOwnerRedirect(user, location.pathname);
    if (redirect && redirect !== location.pathname) {
        return <Navigate to={redirect} replace />;
    }
    return children;
};

/** Auth wrapper for internal-team routes. */
const Protected = ({ children, roles }) => {
    const { user, status } = useAuth();
    const location = useLocation();

    if (status === "checking") return <FullscreenLoader />;
    if (status !== "authenticated" || !user) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    if (user.role === "SHOP_OWNER") {
        const redirect = shopOwnerRedirect(user, location.pathname);
        if (redirect) return <Navigate to={redirect} replace />;
    }
    if (user.role === "CUSTOMER") {
        return <Navigate to={user.district_slug ? `/home?district_slug=${user.district_slug}` : "/customer/complete-profile"} replace />;
    }

    if (roles && !roles.includes(user.role)) {
        setTimeout(() => toast.error("You don't have access to that page."), 0);
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

/** Auth wrapper for /customer/* routes. Requires CUSTOMER role. */
const CustomerProtected = ({ children }) => {
    const { user, status } = useAuth();
    const location = useLocation();
    if (status === "checking") return <FullscreenLoader />;
    if (status !== "authenticated" || !user) {
        return <Navigate to="/customer/login" replace state={{ from: location }} />;
    }
    if (user.role !== "CUSTOMER") {
        if (user.role === "SHOP_OWNER") return <Navigate to="/merchant/home" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    if (!user.district_slug && location.pathname !== "/customer/complete-profile") {
        return <Navigate to="/customer/complete-profile" replace />;
    }
    return children;
};

const FullscreenLoader = () => (
    <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-orange-500" size={28} />
    </div>
);

const Shell = ({ children }) => <AppShell>{children}</AppShell>;

const StandaloneLayout = ({ children }) => (
    <div className="min-h-screen bg-neutral-50" data-testid="public-layout">
        <header className="border-b border-neutral-200 bg-white">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 lg:px-8">
                <Link to="/">
                    <Logo size={32} />
                </Link>
                <div className="flex items-center gap-3">
                    <CityChip />
                    <Link to="/login">
                        <Button variant="outline" size="sm" data-testid="public-signin-btn">
                            Sign in
                        </Button>
                    </Link>
                </div>
            </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 lg:px-8">{children}</main>
    </div>
);

const PublicOrShell = ({ children }) => {
    const { user, status } = useAuth();
    if (status === "checking") return <FullscreenLoader />;
    if (status === "authenticated" && user) return <Shell>{children}</Shell>;
    return <StandaloneLayout>{children}</StandaloneLayout>;
};

/** Phase 9a — gate anonymous /posts to onboarding until a location is set. */
const OnboardingGate = ({ children }) => {
    const { user } = useAuth();
    const loc = getLocalLocation();
    const hasLoc = loc?.district_slug || user?.district_slug;
    if (!hasLoc) return <Navigate to="/welcome" replace />;
    return children;
};

/** Phase 14 — Customer welcome onboarding gate.
 * If the visitor is unauthenticated AND has never seen the splash flow,
 * bounce them to /onboarding before showing the home surface. Logged-in
 * users always skip this. */
const CUSTOMER_ONBOARDING_FLAG = "offernest_onboarded";
const CustomerOnboardingGate = ({ children }) => {
    const { user, status } = useAuth();
    if (status === "checking") return <FullscreenLoader />;
    const isAuthed = status === "authenticated" && !!user;
    if (!isAuthed) {
        let done = false;
        try { done = localStorage.getItem(CUSTOMER_ONBOARDING_FLAG) === "true"; }
        catch { /* ignore */ }
        if (!done) return <Navigate to="/onboarding" replace />;
    }
    return children;
};

function AppInner() {
    return (
        <Routes>
            <Route path="/welcome" element={<Onboarding />} />
            <Route path="/onboarding" element={<OnboardingWelcome />} />
            <Route
                path="/home"
                element={
                    <CustomerOnboardingGate>
                        <OnboardingGate>
                            <CustomerHome />
                        </OnboardingGate>
                    </CustomerOnboardingGate>
                }
            />
            {/* Public partner shops routes — no auth gate (API is public). */}
            <Route path="/partner-shops" element={<CustomerPartnerShopsPage />} />
            <Route path="/partner-shops/:id" element={<CustomerPartnerShopDetailPage />} />
            {/* Public / internal auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Merchant portal — public routes */}
            <Route path="/merchant/login" element={<MerchantLogin />} />
            <Route path="/merchant/register" element={<MerchantRegister />} />

            {/* Merchant portal — protected routes */}
            <Route
                path="/merchant"
                element={<Navigate to="/merchant/home" replace />}
            />
            <Route
                path="/merchant/onboarding"
                element={
                    <MerchantProtected>
                        <MerchantOnboarding />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/home"
                element={
                    <MerchantProtected>
                        <MerchantHome />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/offers"
                element={
                    <MerchantProtected>
                        <MerchantOffers />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/ads"
                element={
                    <MerchantProtected>
                        <MerchantAdsPage />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/offers/new"
                element={
                    <MerchantProtected>
                        <MerchantOfferNew />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/offers/:id"
                element={
                    <MerchantProtected>
                        <MerchantOfferDetail />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/coupons"
                element={
                    <MerchantProtected>
                        <MerchantCoupons />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/profile"
                element={
                    <MerchantProtected>
                        <MerchantProfile />
                    </MerchantProtected>
                }
            />
            <Route
                path="/merchant/videos"
                element={
                    <MerchantProtected>
                        <MerchantVideos />
                    </MerchantProtected>
                }
            />

            {/* Legacy shop-owner onboarding (Google flow) */}
            <Route
                path="/onboarding/shop"
                element={
                    <Protected roles={["SHOP_OWNER"]}>
                        <OnboardingShop />
                    </Protected>
                }
            />
            <Route
                path="/pending"
                element={
                    <Protected roles={["SHOP_OWNER"]}>
                        <PendingPage />
                    </Protected>
                }
            />
            <Route
                path="/rejected"
                element={
                    <Protected roles={["SHOP_OWNER"]}>
                        <RejectedPage />
                    </Protected>
                }
            />

            {/* Internal team routes */}
            <Route
                path="/dashboard"
                element={
                    <Protected>
                        <Shell>
                            <DashboardPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/posts"
                element={
                    <OnboardingGate>
                        <PublicOrShell>
                            <PostsPage mode="app" />
                        </PublicOrShell>
                    </OnboardingGate>
                }
            />
            <Route
                path="/posts/compare"
                element={
                    <OnboardingGate>
                        <ComparePricesPage />
                    </OnboardingGate>
                }
            />
            <Route
                path="/customer/bus-booking"
                element={
                    <OnboardingGate>
                        <BusBookingPage />
                    </OnboardingGate>
                }
            />
            <Route
                path="/posts/new"
                element={
                    <Protected
                        roles={[
                            "SUPER_ADMIN",
                            "FRANCHISE_OWNER",
                            "DISTRICT_MANAGER",
                            "SUPERVISOR",
                            "STAFF",
                            "SHOP_OWNER",
                        ]}
                    >
                        <Shell>
                            <PostCreatePage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/posts/:id"
                element={
                    <PublicOrShell>
                        <PostDetailPage />
                    </PublicOrShell>
                }
            />
            <Route
                path="/me/posts"
                element={
                    <Protected>
                        <Shell>
                            <MyPostsPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/settings"
                element={
                    <Protected>
                        <Shell>
                            <SettingsPage />
                        </Shell>
                    </Protected>
                }
            />

            {/* Admin */}
            <Route
                path="/admin/users"
                element={
                    <Protected roles={["SUPER_ADMIN"]}>
                        <Shell>
                            <AdminUsersPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/shops/pending"
                element={
                    <Protected roles={["SUPER_ADMIN"]}>
                        <Shell>
                            <AdminShopsPendingPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/waitlist"
                element={
                    <Protected roles={["SUPER_ADMIN", "ACCOUNTS"]}>
                        <Shell>
                            <AdminWaitlistPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/analytics"
                element={
                    <Protected roles={["SUPER_ADMIN", "ACCOUNTS"]}>
                        <Shell>
                            <AdminAnalyticsPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/partner-shops"
                element={
                    <Protected roles={["SUPER_ADMIN"]}>
                        <Shell>
                            <AdminPartnerShopsPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/merchant-ads"
                element={
                    <Protected roles={["SUPER_ADMIN"]}>
                        <Shell>
                            <AdminMerchantAdsPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/partner-brands"
                element={
                    <Protected roles={["SUPER_ADMIN"]}>
                        <Shell>
                            <AdminPartnerBrandsPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/admin/posts"
                element={
                    <Protected roles={["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR"]}>
                        <Shell>
                            <AdminPostsPage />
                        </Shell>
                    </Protected>
                }
            />
            <Route
                path="/smart-home"
                element={
                    <Protected roles={["SUPER_ADMIN"]}>
                        <Shell>
                            <SmartHomePage />
                        </Shell>
                    </Protected>
                }
            />

            {/* Revenue admin (SUPER_ADMIN + ACCOUNTS) */}
            {[
                { path: "/admin/revenue", el: <RevenueHomePage /> },
                { path: "/admin/revenue/packages", el: <PackagesPage /> },
                { path: "/admin/revenue/subscriptions", el: <SubscriptionsPage /> },
                { path: "/admin/revenue/transactions", el: <TransactionsPage /> },
                { path: "/admin/revenue/coupon-topups", el: <CouponTopupsPage /> },
                { path: "/admin/revenue/reports", el: <ReportsPage /> },
            ].map((r) => (
                <Route
                    key={r.path}
                    path={r.path}
                    element={
                        <Protected roles={["SUPER_ADMIN", "ACCOUNTS"]}>
                            <RevenueGuard>
                                <Shell>{r.el}</Shell>
                            </RevenueGuard>
                        </Protected>
                    }
                />
            ))}

            {/* Ads admin (Phase 4b) — SUPER_ADMIN + ACCOUNTS */}
            {[
                { path: "/admin/ads", el: <AdsOverviewPage /> },
                { path: "/admin/ads/banners", el: <BannersManagerPage /> },
                { path: "/admin/ads/videos", el: <VideoQueuePage /> },
            ].map((r) => (
                <Route
                    key={r.path}
                    path={r.path}
                    element={
                        <Protected roles={["SUPER_ADMIN", "ACCOUNTS"]}>
                            <Shell>{r.el}</Shell>
                        </Protected>
                    }
                />
            ))}
            <Route
                path="/admin/ads/sponsored"
                element={
                    <Protected roles={["SUPER_ADMIN", "ACCOUNTS"]}>
                        <Shell><SponsoredListingsPage /></Shell>
                    </Protected>
                }
            />

            {/* Finance admin (Phase 5b) — SUPER_ADMIN + ACCOUNTS */}
            {[
                { path: "/admin/finance", el: <FinanceOverviewPage /> },
                { path: "/admin/finance/expenses", el: <ExpensesPage /> },
                { path: "/admin/finance/pnl", el: <PnlPage /> },
                { path: "/admin/finance/ai-accountant", el: <AiAccountantPage /> },
                { path: "/admin/reports/partner-clicks", el: <PartnerClicksReportPage /> },
            ].map((r) => (
                <Route
                    key={r.path}
                    path={r.path}
                    element={
                        <Protected roles={["SUPER_ADMIN", "ACCOUNTS"]}>
                            <Shell>{r.el}</Shell>
                        </Protected>
                    }
                />
            ))}

            {/* Phase 7b — Trending admin (SUPER_ADMIN + ACCOUNTS + DM) */}
            <Route
                path="/admin/trending"
                element={
                    <Protected roles={["SUPER_ADMIN", "ACCOUNTS", "DISTRICT_MANAGER"]}>
                        <Shell><TrendingReport /></Shell>
                    </Protected>
                }
            />

            {/* Phase 7b — Merchant QR scanner */}
            <Route
                path="/merchant/coupons/scan"
                element={
                    <MerchantProtected>
                        <MerchantScan />
                    </MerchantProtected>
                }
            />

            {/* Phase 7b — Customer routes */}
            <Route path="/customer/login" element={<CustomerLogin />} />
            <Route path="/customer/register" element={<CustomerRegister />} />
            <Route
                path="/customer/complete-profile"
                element={
                    <CustomerProtected>
                        <CustomerCompleteProfile />
                    </CustomerProtected>
                }
            />
            {[
                { path: "/customer/favorites", el: <CustomerFavorites /> },
                { path: "/customer/coupons", el: <CustomerCoupons /> },
                { path: "/customer/profile", el: <CustomerProfile /> },
            ].map((r) => (
                <Route
                    key={r.path}
                    path={r.path}
                    element={
                        <CustomerProtected>
                            <CustomerLayout>{r.el}</CustomerLayout>
                        </CustomerProtected>
                    }
                />
            ))}

            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
        </Routes>
    );
}

function RootRedirect() {
    const { status, user } = useAuth();
    if (status === "checking") return <FullscreenLoader />;
    if (status === "authenticated" && user) {
        if (user.role === "SHOP_OWNER") {
            const redirect = shopOwnerRedirect(user, "/") || "/merchant/home";
            return <Navigate to={redirect} replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }
    // Phase 14 — first-time unauthenticated visitors see the welcome flow.
    let onboarded = false;
    try { onboarded = localStorage.getItem(CUSTOMER_ONBOARDING_FLAG) === "true"; }
    catch { /* ignore */ }
    if (!onboarded) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/login" replace />;
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <SplashScreen />
                <AppInner />
                <Toaster
                    position="top-center"
                    richColors
                    closeButton
                    toastOptions={{
                        classNames: {
                            toast: "rounded-xl",
                        },
                    }}
                />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
